/**
 * Notification sound presets — pure Web Audio API (no audio files).
 * Each preset is a function `play()` and a metadata blob for the picker.
 *
 * Naming: short kebab id, human label, kind (tells Settings which event
 * this preset is appropriate for — but the admin can still mix any preset
 * with any event).
 */

let _ctx = null;
function getCtx() {
  if (typeof window === 'undefined') return null;
  if (!_ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    _ctx = new AC();
  }
  if (_ctx.state === 'suspended') { _ctx.resume().catch(() => {}); }
  return _ctx;
}

function tone(freq, when, dur, gain = 0.06, type = 'sine') {
  const ctx = getCtx(); if (!ctx) return;
  const osc = ctx.createOscillator();
  const env = ctx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  env.gain.setValueAtTime(0.0001, when);
  env.gain.exponentialRampToValueAtTime(gain, when + 0.008);
  env.gain.exponentialRampToValueAtTime(0.0001, when + dur);
  osc.connect(env).connect(ctx.destination);
  osc.start(when); osc.stop(when + dur + 0.05);
}

// ---------- Preset functions ----------
const playSilence = () => { /* noop — explicitly chosen "off" */ };

const playAlertDescend = () => {
  const ctx = getCtx(); if (!ctx) return; const t = ctx.currentTime;
  tone(466.16, t,         0.18, 0.07, 'triangle'); // B♭4
  tone(349.23, t + 0.20,  0.30, 0.08, 'triangle'); // F4
};
const playAlertHigh = () => {
  const ctx = getCtx(); if (!ctx) return; const t = ctx.currentTime;
  tone(880.00, t,        0.12, 0.06, 'square');
  tone(880.00, t + 0.18, 0.12, 0.06, 'square');
  tone(880.00, t + 0.36, 0.20, 0.07, 'square');
};
const playAlertSiren = () => {
  const ctx = getCtx(); if (!ctx) return; const t = ctx.currentTime;
  tone(523, t,        0.15, 0.06, 'sawtooth');
  tone(659, t + 0.16, 0.15, 0.06, 'sawtooth');
  tone(523, t + 0.32, 0.15, 0.06, 'sawtooth');
};

// 1.5-second wailing brute-force alarm — three rising/falling siren sweeps
// using sawtooth pulses + sub-bass to grab attention on a busy admin desk.
const playBruteForceAlarm = () => {
  const ctx = getCtx(); if (!ctx) return; const t0 = ctx.currentTime;
  for (let i = 0; i < 3; i++) {
    const t = t0 + i * 0.50;
    tone(440, t,        0.18, 0.09, 'sawtooth');
    tone(880, t + 0.18, 0.18, 0.09, 'sawtooth');
    tone(110, t,        0.36, 0.08, 'square'); // sub
  }
};

const playChimeMajor = () => {
  const ctx = getCtx(); if (!ctx) return; const t = ctx.currentTime;
  tone(523.25, t,        0.12, 0.05); tone(659.25, t + 0.10, 0.12, 0.05);
  tone(783.99, t + 0.20, 0.30, 0.07);
};
const playChimeBell = () => {
  const ctx = getCtx(); if (!ctx) return; const t = ctx.currentTime;
  tone(987.77, t,        0.30, 0.07); // B5
  tone(1318.5, t + 0.05, 0.30, 0.04);
};
const playChimeArpeggio = () => {
  const ctx = getCtx(); if (!ctx) return; const t = ctx.currentTime;
  tone(440, t,        0.10, 0.04);
  tone(554, t + 0.08, 0.10, 0.05);
  tone(659, t + 0.16, 0.10, 0.06);
  tone(880, t + 0.24, 0.20, 0.07);
};

const playChaChing = () => {
  const ctx = getCtx(); if (!ctx) return; const t = ctx.currentTime;
  tone(1318.51, t,        0.08, 0.05);
  tone(1760.00, t + 0.07, 0.20, 0.07);
};
const playCoinDrop = () => {
  const ctx = getCtx(); if (!ctx) return; const t = ctx.currentTime;
  tone(1568, t,        0.05, 0.05); // G6
  tone(2093, t + 0.05, 0.05, 0.05);
  tone(1568, t + 0.12, 0.10, 0.04);
};
const playMoneyRush = () => {
  const ctx = getCtx(); if (!ctx) return; const t = ctx.currentTime;
  tone(1108, t,        0.08, 0.05);
  tone(1396, t + 0.07, 0.08, 0.05);
  tone(1760, t + 0.14, 0.16, 0.06);
  tone(2093, t + 0.22, 0.20, 0.06);
};

const playSoftDing = () => {
  const ctx = getCtx(); if (!ctx) return; const t = ctx.currentTime;
  tone(1046.5, t, 0.18, 0.05);
};
const playMessagePop = () => {
  const ctx = getCtx(); if (!ctx) return; const t = ctx.currentTime;
  tone(659, t,        0.06, 0.04);
  tone(880, t + 0.06, 0.10, 0.05);
};
const playWhistle = () => {
  const ctx = getCtx(); if (!ctx) return; const t = ctx.currentTime;
  tone(1760, t,        0.10, 0.04);
  tone(2349, t + 0.10, 0.18, 0.05);
};

// ---------- Catalogue ----------
// `kind` is just a hint for the picker ordering — any preset works for any
// event, the admin can mix-and-match freely.
export const SOUND_PRESETS = [
  { id: 'silence',         label: 'Silent (off)',                kind: 'any',     play: playSilence },
  { id: 'alert_descend',   label: 'Two-tone descending alert',   kind: 'alert',   play: playAlertDescend },
  { id: 'alert_high',      label: 'High-pitched warning',        kind: 'alert',   play: playAlertHigh },
  { id: 'alert_siren',     label: 'Siren bursts',                kind: 'alert',   play: playAlertSiren },
  { id: 'brute_force_alarm', label: 'Brute-force alarm (wailing siren)', kind: 'alert', play: playBruteForceAlarm },
  { id: 'chime_major',     label: 'C-E-G major triad chime',     kind: 'good',    play: playChimeMajor },
  { id: 'chime_bell',      label: 'Bright bell',                 kind: 'good',    play: playChimeBell },
  { id: 'chime_arp',       label: 'Ascending arpeggio',          kind: 'good',    play: playChimeArpeggio },
  { id: 'cha_ching',       label: 'Cha-ching cash register',     kind: 'money',   play: playChaChing },
  { id: 'coin_drop',       label: 'Coin drop',                   kind: 'money',   play: playCoinDrop },
  { id: 'money_rush',      label: 'Money rush',                  kind: 'money',   play: playMoneyRush },
  { id: 'soft_ding',       label: 'Soft ding',                   kind: 'message', play: playSoftDing },
  { id: 'message_pop',     label: 'Message pop',                 kind: 'message', play: playMessagePop },
  { id: 'whistle',         label: 'Quick whistle',               kind: 'message', play: playWhistle },
];

export const PRESET_BY_ID = Object.fromEntries(SOUND_PRESETS.map((p) => [p.id, p]));

// Default preset per event-type — applied if admin hasn't customised.
export const DEFAULT_SOUND_FOR = {
  login_failed:    'alert_descend',
  login_brute:     'brute_force_alarm',
  order_new:       'chime_major',
  recharge_new:    'cha_ching',
  ticket_message:  'soft_ding',
};

/** Resolve and play. Falls back to the type's default if the saved id is gone
 * (e.g. preset was renamed). Returns true if a sound was actually played. */
export function playForEvent(eventType, idMap = {}) {
  const id = idMap[eventType] || DEFAULT_SOUND_FOR[eventType];
  const preset = PRESET_BY_ID[id] || PRESET_BY_ID[DEFAULT_SOUND_FOR[eventType]];
  if (!preset || preset.id === 'silence') return false;
  try { preset.play(); return true; } catch (_) { return false; }
}

// Backwards compatibility — keep the named exports the existing LiveLoginFeed
// uses while we migrate it to the new picker-aware API.
export const playFailedLogin = playAlertDescend;
export const playOrderNew    = playChimeMajor;
export const playRecharge    = playChaChing;
